import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { CalendarRange, RefreshCw } from "lucide-react";
import { Card } from "@/components/admin/ui";
import { API } from "@/config";
import { RefundQueue } from "@/pages/admin/RefundQueue";
import { ChangeRequests } from "@/pages/admin/ChangeRequests";

/**
 * Owner Inbox — single tab-switching surface for every customer request
 * the owner needs to sign off on. Both queues (Refunds + Date changes)
 * share the same admin approve/deny workflow so consolidating them into
 * one page means one URL to check every morning.
 *
 * Deep-linking: `?tab=refunds` and `?tab=changes` are supported so the
 * dashboard tiles can jump directly to the right list.
 */
export const OwnerInbox = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") === "refunds" ? "refunds" : "changes";
  const [tab, setTab] = useState(initialTab);
  const [counts, setCounts] = useState({ refunds: null, changes: null });

  const loadCounts = useCallback(async () => {
    try {
      const [rq, cq] = await Promise.all([
        axios.get(`${API}/admin/refund-queue`),
        axios.get(`${API}/admin/change-requests`),
      ]);
      setCounts({
        refunds: (rq.data?.requests || []).length,
        changes: (cq.data?.requests || []).length,
      });
    } catch { /* silent */ }
  }, []);
  useEffect(() => { loadCounts(); }, [loadCounts]);

  const switchTab = (next) => {
    setTab(next);
    setSearchParams({ tab: next }, { replace: true });
  };

  const tabs = [
    { key: "changes", label: "Date Changes", icon: CalendarRange, count: counts.changes },
    { key: "refunds", label: "Refunds", icon: RefreshCw, count: counts.refunds },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-4xl">Owner Inbox</h1>
          <p className="text-xs text-[#B9C8D5] mt-1 uppercase tracking-[0.22em]">Everything waiting on your sign-off</p>
        </div>
      </div>

      <Card className="p-1 mb-6 inline-flex gap-1" data-testid="owner-inbox-tabs">
        {tabs.map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => switchTab(t.key)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm transition-colors ${active ? "bg-[#FFD166]/15 text-[#FFD166] border border-[#FFD166]/40" : "text-[#B9C8D5] hover:text-[#F6F1E4] hover:bg-white/5 border border-transparent"}`}
              data-testid={`owner-inbox-tab-${t.key}`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
              {t.count !== null && t.count > 0 && (
                <span
                  className={`inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full text-[10px] font-semibold border ${active ? "bg-[#FFD166]/25 text-[#FFD166] border-[#FFD166]/50" : "bg-red-500/80 text-[#F6F1E4] border-red-400/60"}`}
                  data-testid={`owner-inbox-tab-count-${t.key}`}
                >
                  {t.count > 99 ? "99+" : t.count}
                </span>
              )}
            </button>
          );
        })}
      </Card>

      {tab === "changes" ? <ChangeRequests embedded /> : <RefundQueue embedded />}
    </div>
  );
};
